// src/pages/Contact.js - Redesigned to match main design
import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Mail, Phone, MapPin, Send, MessageCircle, 
  Twitter, Instagram, Youtube, Facebook, Clock,
  CheckCircle, AlertCircle
} from "lucide-react";
import SEO from "../components/SEO";

/* ═══════════════════════════════════════════════════════════════════
   GLOBAL STYLES - Matching Home.js design system
══════════════════════════════════════════════════════════════════ */
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@700;900&display=swap');
    @font-face { font-family:'Azonix'; src:url('/fonts/Azonix.otf') format('opentype'),url('/fonts/azonix.woff2') format('woff2'); font-display:swap; }
    @font-face { font-family:'Japanese3017'; src:url('/fonts/Japanese-3017.ttf') format('truetype'); font-display:swap; }
    @font-face { font-family:'Acknowledgement'; src:url('/fonts/Acknowledgement.otf') format('opentype'); font-display:swap; }

    :root {
      --az: 'Azonix','Impact',sans-serif;
      --jp: 'Japanese3017','Noto Serif JP',serif;
      --wt: 'Acknowledgement','Azonix',sans-serif;
      --ui: -apple-system,'Helvetica Neue',sans-serif;
      --bd: 'Georgia',serif;
      --neu: #a78bfa;
      --bg: #08080c;
      --glass: rgba(255,255,255,0.04);
      --gb: rgba(255,255,255,0.08);
    }

    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}

    .contact-root {
      background: var(--bg);
      color: #fff;
      font-family: var(--ui);
      overflow-x: hidden;
      min-height: 100vh;
      position: relative;
    }

    .contact-wrap {
      max-width: 1240px;
      margin: 0 auto;
      padding: 0 clamp(16px,4vw,52px);
      position: relative;
      z-index: 1;
    }

    .contact-sec { padding: clamp(56px,8vh,100px) 0; }
    .contact-sec-sm { padding: clamp(36px,5vh,64px) 0; }

    /* Section Header */
    .contact-sh {
      display: flex;
      align-items: center;
      margin-bottom: 28px;
    }
    .contact-sh-left { display:flex; align-items:center; gap:12px; }
    .contact-sh-bar { width:3px; height:26px; border-radius:2px; flex-shrink:0; }
    .contact-sh-label {
      font-family: var(--az);
      font-size: 16px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }

    /* Glass Card */
    .contact-card {
      background: var(--glass);
      backdrop-filter: blur(24px);
      border: 1px solid var(--gb);
      border-radius: 18px;
      padding: clamp(24px,3vw,36px);
    }

    /* Form */
    .contact-form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
    }
    @media(max-width:900px){ .contact-form-grid { grid-template-columns: 1fr; } }

    .contact-form-card {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 18px;
      padding: clamp(24px,3vw,36px);
    }
    .contact-form-title {
      font-family: var(--az);
      font-size: 20px;
      color: #fff;
      margin-bottom: 24px;
    }

    .contact-field {
      margin-bottom: 18px;
    }
    .contact-label {
      display: block;
      font-size: 12px;
      color: rgba(255,255,255,0.5);
      margin-bottom: 8px;
      font-weight: 500;
    }
    .contact-input, .contact-textarea, .contact-select {
      width: 100%;
      padding: 14px 16px;
      background: rgba(255,255,255,0.04);
      border: 1px solid var(--gb);
      border-radius: 10px;
      color: #fff;
      font-size: 14px;
      font-family: var(--ui);
      outline: none;
      transition: border-color .2s;
    }
    .contact-input::placeholder, .contact-textarea::placeholder {
      color: rgba(255,255,255,0.25);
    }
    .contact-input:focus, .contact-textarea:focus, .contact-select:focus {
      border-color: rgba(167,139,250,0.4);
    }
    .contact-textarea {
      min-height: 120px;
      resize: vertical;
    }
    .contact-select {
      cursor: pointer;
    }
    .contact-select option {
      background: var(--bg);
    }

    .contact-submit {
      width: 100%;
      padding: 14px;
      border-radius: 10px;
      border: none;
      background: linear-gradient(135deg, var(--neu), #8b5cf6);
      color: #fff;
      font-family: var(--az);
      font-size: 10px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      cursor: pointer;
      transition: all .25s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    .contact-submit:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(167,139,250,0.4);
    }
    .contact-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .contact-submit-loading {
      width: 18px; height: 18px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin .8s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    .contact-success {
      padding: 16px;
      background: rgba(74,222,128,0.1);
      border: 1px solid rgba(74,222,128,0.2);
      border-radius: 10px;
      display: flex;
      align-items: center;
      gap: 12px;
      color: #4ade80;
      font-size: 14px;
    }
    .contact-error {
      font-size: 11px;
      color: #f87171;
      display: flex;
      align-items: center;
      gap: 4px;
      margin-top: 6px;
    }

    /* Info Cards */
    .contact-info-grid {
      display: grid;
      gap: 14px;
    }

    .contact-info-card {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 14px;
      padding: 20px;
      transition: all .3s ease;
    }
    .contact-info-card:hover {
      border-color: rgba(167,139,250,0.25);
    }
    .contact-info-card:hover .contact-info-icon {
      transform: scale(1.05);
    }
    .contact-info-icon {
      width: 44px; height: 44px;
      border-radius: 12px;
      background: linear-gradient(135deg, rgba(167,139,250,0.15), rgba(167,139,250,0.05));
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 14px;
      transition: transform .3s ease;
    }
    .contact-info-title {
      font-family: var(--az);
      font-size: 14px;
      color: #fff;
      margin-bottom: 4px;
    }
    .contact-info-value {
      font-size: 14px;
      color: var(--neu);
      margin-bottom: 4px;
    }
    .contact-info-desc {
      font-size: 12px;
      color: rgba(255,255,255,0.38);
    }

    /* Map Placeholder */
    .contact-map {
      position: relative;
      border-radius: 16px;
      overflow: hidden;
      height: 180px;
      margin-top: 14px;
    }
    .contact-map-bg {
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(236,72,153,0.08) 100%);
    }
    .contact-map-pattern {
      position: absolute;
      inset: 0;
      background-image: radial-gradient(circle, rgba(167,85,247,0.15) 1px, transparent 1px);
      background-size: 16px 16px;
    }
    .contact-map-content {
      position: relative;
      z-index: 1;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
    .contact-map-title {
      font-family: var(--az);
      font-size: 16px;
      color: #fff;
      margin-bottom: 4px;
    }
    .contact-map-sub {
      font-size: 12px;
      color: rgba(255,255,255,0.42);
    }
    .contact-map-badge {
      font-size: 10px;
      color: var(--neu);
      margin-top: 8px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }

    /* Response Time */
    .contact-response {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 12px;
      padding: 16px;
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .contact-response-icon {
      width: 40px; height: 40px;
      border-radius: 10px;
      background: linear-gradient(135deg, rgba(167,139,250,0.12), rgba(167,139,250,0.05));
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .contact-response-title {
      font-size: 14px;
      font-weight: 600;
      color: #fff;
      margin-bottom: 2px;
    }
    .contact-response-desc {
      font-size: 12px;
      color: rgba(255,255,255,0.42);
    }

    /* Social Links */
    .contact-social {
      margin-top: 14px;
    }
    .contact-social-title {
      font-family: var(--az);
      font-size: 13px;
      color: #fff;
      margin-bottom: 14px;
    }
    .contact-social-links {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
    .contact-social-link {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 10px;
      color: rgba(255,255,255,0.6);
      font-size: 13px;
      text-decoration: none;
      transition: all .25s;
    }
    .contact-social-link:hover {
      border-color: rgba(167,139,250,0.4);
      color: #fff;
      transform: translateY(-2px);
    }

    /* CTA Section */
    .contact-cta {
      max-width: 800px;
      margin: 0 auto;
      border-radius: 22px;
      text-align: center;
      background: linear-gradient(135deg, rgba(167,139,250,0.08) 0%, rgba(236,72,153,0.06) 100%);
      border: 1px solid rgba(255,255,255,0.08);
      padding: clamp(32px,5vw,56px) clamp(22px,4vw,40px);
      position: relative;
      overflow: hidden;
    }
    .contact-cta-glow {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%,-50%);
      width: 400px; height: 180px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(167,139,250,0.1), transparent);
      filter: blur(50px);
      pointer-events: none;
    }
    .contact-cta-title {
      position: relative;
      z-index: 1;
      font-family: var(--az);
      font-size: clamp(18px,3vw,26px);
      color: #fff;
      margin-bottom: 12px;
    }
    .contact-cta-desc {
      position: relative;
      z-index: 1;
      font-size: 14px;
      color: rgba(255,255,255,0.42);
      line-height: 1.8;
      max-width: 480px;
      margin: 0 auto 24px;
    }
    .contact-cta-btns {
      position: relative;
      z-index: 1;
      display: flex;
      gap: 12px;
      justify-content: center;
      flex-wrap: wrap;
    }
    .contact-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      text-decoration: none;
      font-family: var(--az);
      font-size: 10px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      transition: all .25s;
      white-space: nowrap;
    }
    .contact-btn-whatsapp {
      background: linear-gradient(135deg, #22c55e, #16a34a);
      color: #fff;
    }
    .contact-btn-whatsapp:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(34,197,94,0.3);
    }
    .contact-btn-discord {
      border: 1px solid rgba(99,102,241,0.32);
      color: #6366f1;
    }
    .contact-btn-discord:hover {
      background: rgba(99,102,241,0.1);
    }

    /* Hero */
    .contact-hero {
      position: relative;
      min-height: 40vh;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    .contact-hero-bg {
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(167,139,250,0.08) 0%, rgba(236,72,153,0.04) 50%, rgba(99,102,241,0.06) 100%);
    }
    .contact-hero-glow1 {
      position: absolute;
      top: 20%; left: 15%;
      width: 260px; height: 260px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(167,139,250,0.12), transparent);
      filter: blur(60px);
    }
    .contact-hero-glow2 {
      position: absolute;
      bottom: 20%; right: 15%;
      width: 200px; height: 200px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(236,72,153,0.1), transparent);
      filter: blur(50px);
    }
    .contact-hero-content {
      position: relative;
      z-index: 1;
      text-align: center;
      padding: 80px 20px 40px;
    }
    .contact-hero-title {
      font-family: var(--az);
      font-size: clamp(36px,6vw,58px);
      letter-spacing: -1px;
      margin-bottom: 16px;
    }
    .contact-hero-title span {
      background: linear-gradient(135deg, var(--neu), #ec4899);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .contact-hero-desc {
      font-size: clamp(14px,1.5vw,17px);
      color: rgba(255,255,255,0.42);
      max-width: 460px;
      margin: 0 auto;
      line-height: 1.8;
    }

    /* Divider */
    .contact-div {
      height: 1px;
      background: linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent);
      margin: 0 0 clamp(48px,7vh,80px);
    }
  `}</style>
);

/* ═══════════════════════════════════════════════════════════════════
   PARTICLE BACKGROUND
══════════════════════════════════════════════════════════════════ */
const ParticleBackground = () => {
  const ref = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const raf = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    let W = cv.width = window.innerWidth;
    let H = cv.height = window.innerHeight;

    const resize = () => { W = cv.width = window.innerWidth; H = cv.height = window.innerHeight; };
    window.addEventListener('resize', resize);

    const pts = Array.from({ length: 35 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - .5) * .2, vy: (Math.random() - .5) * .2,
      r: Math.random() * 1.2 + .3, a: Math.random() * .35 + .07,
      h: [250, 270, 280][Math.floor(Math.random() * 3)],
    }));

    const mm = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', mm);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const { x: mx, y: my } = mouse.current;
      for (let i = 0; i < pts.length; i++)
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < 100) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.strokeStyle = `rgba(167,139,250,${.05 * (1 - d / 100)})`; ctx.lineWidth = .4; ctx.stroke(); }
        }
      pts.forEach(p => {
        const d = Math.hypot(mx - p.x, my - p.y);
        if (d < 90) { p.vx -= (mx - p.x) / d * .028; p.vy -= (my - p.y) / d * .028; }
        p.vx *= .99; p.vy *= .99; p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.h},72%,70%,${p.a})`; ctx.fill();
      });
      const g = ctx.createRadialGradient(mx, my, 0, mx, my, 160);
      g.addColorStop(0, 'rgba(167,139,250,0.04)'); g.addColorStop(1, 'transparent');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      raf.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener('resize', resize); window.removeEventListener('mousemove', mm); cancelAnimationFrame(raf.current); };
  }, []);

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: .5 }} />;
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
export default function Contact() {
  const [form, setForm] = useState({ 
    name: "", 
    email: "", 
    subject: "general",
    message: "" 
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.name.trim()) {
      newErrors.name = "Name is required";
    } else if (form.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!form.message.trim()) {
      newErrors.message = "Message is required";
    } else if (form.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setSubmitStatus("success");
    setForm({ name: "", email: "", subject: "general", message: "" });
    
    // Clear success message after 5 seconds
    setTimeout(() => setSubmitStatus(null), 5000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const contactInfo = [
    { 
      icon: Mail, 
      title: "Email Us", 
      value: "hello@animac-metaverse.com",
      description: "We'll respond within 24 hours"
    },
    { 
      icon: Phone, 
      title: "Call Us", 
      value: "+1 (555) 123-4567",
      description: "Mon-Fri, 9AM - 6PM EST"
    },
    { 
      icon: MapPin, 
      title: "Visit Us", 
      value: "Lagos, Nigeria",
      description: "Global headquarters"
    }
  ];

  const socialLinks = [
    { icon: MessageCircle, name: "WhatsApp", href: "https://whatsapp.com" },
    { icon: Twitter, name: "Twitter", href: "https://twitter.com" },
    { icon: Instagram, name: "Instagram", href: "https://instagram.com" },
    { icon: Youtube, name: "YouTube", href: "https://youtube.com" },
    { icon: Facebook, name: "Facebook", href: "https://facebook.com" }
  ];

  const subjects = [
    { value: "general", label: "General Inquiry" },
    { value: "support", label: "Technical Support" },
    { value: "partnership", label: "Partnership" },
    { value: "press", label: "Press & Media" },
    { value: "feedback", label: "Feedback" }
  ];

  return (
    <>
      <PageStyles />
      <SEO
        title="Contact ANIMAC - Get in Touch"
        description="Have questions or feedback? Reach out to the ANIMAC team. We're here to help and would love to hear from you!"
        url="/contact"
        image="/assets/animac-preview-logo.svg"
        type="website"
      />
      
      <ParticleBackground />

      <div className="contact-root">
        {/* Hero */}
        <div className="contact-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1920&q=80" 
              alt="Contact" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/85 via-gray-900/75 to-gray-900/85" />
          </div>
          <div className="contact-hero-bg" />
          <div className="contact-hero-glow1" />
          <div className="contact-hero-glow2" />
          <div className="contact-hero-content">
            <motion.h1
              className="contact-hero-title"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              Get In <span>Touch</span>
            </motion.h1>
            <motion.p
              className="contact-hero-desc"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Have questions, feedback, or just want to say hello? We'd love to hear from you!
            </motion.p>
          </div>
        </div>

        <div className="contact-wrap">
          {/* Main Content */}
          <section className="contact-sec">
            <div className="contact-form-grid">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="contact-form-card">
                  <h2 className="contact-form-title">Send Us a Message</h2>
                  
                  {/* Success Message */}
                  {submitStatus === "success" && (
                    <div className="contact-success">
                      <CheckCircle size={20} />
                      Thank you! Your message has been sent successfully.
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    {/* Name Field */}
                    <div className="contact-field">
                      <label className="contact-label">Your Name</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="contact-input"
                      />
                      {errors.name && (
                        <p className="contact-error">
                          <AlertCircle size={12} /> {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Email Field */}
                    <div className="contact-field">
                      <label className="contact-label">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="contact-input"
                      />
                      {errors.email && (
                        <p className="contact-error">
                          <AlertCircle size={12} /> {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Subject Select */}
                    <div className="contact-field">
                      <label className="contact-label">Subject</label>
                      <select
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        className="contact-select"
                      >
                        {subjects.map(sub => (
                          <option key={sub.value} value={sub.value}>{sub.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Message Field */}
                    <div className="contact-field">
                      <label className="contact-label">Your Message</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Tell us what's on your mind..."
                        className="contact-textarea"
                      />
                      {errors.message && (
                        <p className="contact-error">
                          <AlertCircle size={12} /> {errors.message}
                        </p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="contact-submit"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="contact-submit-loading" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="contact-info-grid"
              >
                {/* Contact Cards */}
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="contact-info-card"
                  >
                    <div className="contact-info-icon">
                      <info.icon size={20} color="#a78bfa" />
                    </div>
                    <h3 className="contact-info-title">{info.title}</h3>
                    <p className="contact-info-value">{info.value}</p>
                    <p className="contact-info-desc">{info.description}</p>
                  </motion.div>
                ))}

                {/* Map Placeholder */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="contact-map"
                >
                  <div className="contact-map-bg" />
                  <div className="contact-map-pattern" />
                  <div className="contact-map-content">
                    <MapPin size={28} color="#a78bfa" style={{ marginBottom: '8px' }} />
                    <p className="contact-map-title">ANIMAC Global HQ</p>
                    <p className="contact-map-sub">Lagos, Nigeria</p>
                    <p className="contact-map-badge">Serving worldwide communities</p>
                  </div>
                </motion.div>

                {/* Response Time */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="contact-response"
                >
                  <div className="contact-response-icon">
                    <Clock size={18} color="#a78bfa" />
                  </div>
                  <div>
                    <p className="contact-response-title">Response Time</p>
                    <p className="contact-response-desc">We typically respond within 24 hours during business days.</p>
                  </div>
                </motion.div>

                {/* Social Media Links */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="contact-social"
                >
                  <h3 className="contact-social-title">Connect With Us</h3>
                  <div className="contact-social-links">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-social-link"
                      >
                        <social.icon size={16} />
                        {social.name}
                      </a>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          <div className="contact-div" />

          {/* CTA Section */}
          <section className="contact-sec-sm">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="contact-cta"
            >
              <div className="contact-cta-glow" />
              <h2 className="contact-cta-title">Join the ANIMAC Community</h2>
              <p className="contact-cta-desc">
                Be part of a global community of anime enthusiasts, creators, and innovators. 
                Connect with fellow fans and stay updated on the latest news!
              </p>
              <div className="contact-cta-btns">
                <a
                  href="https://whatsapp.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-btn contact-btn-whatsapp"
                >
                  <MessageCircle size={16} />
                  Join WhatsApp
                </a>
                <a
                  href="https://discord.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-btn contact-btn-discord"
                >
                  Join Discord
                </a>
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </>
  );
}
